import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ROUTES, ROUTE_PATTERNS } from 'constants/routes';
import { PrivateRoute, GymOwnerRoute, AdminRoute, EventEditorRoute, GuestRoute } from 'app/guards';
import Layout from 'app/Layout';
import RouteErrorBoundary from 'app/RouteErrorBoundary';

const Discover = lazy(() => import('features/events/pages/Discover'));
const EventDetail = lazy(() => import('features/events/pages/EventDetail'));
const CityEvents = lazy(() => import('features/events/pages/CityEvents'));
const SavedEvents = lazy(() => import('features/events/pages/SavedEvents'));
const CreateEvent = lazy(() => import('features/events/pages/CreateEvent'));
const EditEvent = lazy(() => import('features/events/pages/EditEvent'));
const Login = lazy(() => import('features/auth/pages/Login'));
const Signup = lazy(() => import('features/auth/pages/Signup'));
const VerifyEmail = lazy(() => import('features/auth/pages/VerifyEmail'));
const ResetPassword = lazy(() => import('features/auth/pages/ResetPassword'));
const Account = lazy(() => import('features/auth/pages/Account'));
const MyBookings = lazy(() => import('features/bookings/pages/MyBookings'));
const BookingSuccess = lazy(() => import('features/bookings/pages/BookingSuccess'));
const GymDashboard = lazy(() => import('features/gyms/pages/GymDashboard'));
const GymProfile = lazy(() => import('features/gyms/pages/GymProfile'));
const PublicProfile = lazy(() => import('features/gyms/pages/PublicProfile'));
const StripeConnectReturn = lazy(() => import('features/gyms/pages/StripeConnectReturn'));
const StripeConnectRefresh = lazy(() => import('features/gyms/pages/StripeConnectRefresh'));
const Landing = lazy(() => import('features/static/pages/Landing'));
const OpenMatEvents = lazy(() => import('features/static/pages/OpenMatEvents'));
const CampLanding = lazy(() => import('features/static/pages/CampLanding'));
const CampTerms = lazy(() => import('features/static/pages/CampTerms'));
const CampBooked = lazy(() => import('features/static/pages/CampBooked'));
const TermsOfService = lazy(() => import('features/static/pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('features/static/pages/PrivacyPolicy'));
const Impressum = lazy(() => import('features/static/pages/Impressum'));
const CookiePolicy = lazy(() => import('features/static/pages/CookiePolicy'));
const Support = lazy(() => import('features/static/pages/Support'));
const NotFound = lazy(() => import('features/static/pages/NotFound'));
const AdminHub = lazy(() => import('features/admin/pages/AdminHub'));
const AdminEvents = lazy(() => import('features/admin/pages/AdminEvents'));
const AdminOwnEvents = lazy(() => import('features/admin/pages/AdminOwnEvents'));
const AdminGyms = lazy(() => import('features/admin/pages/AdminGyms'));
const AdminGymClaims = lazy(() => import('features/admin/pages/AdminGymClaims'));
const AdminGymApprovals = lazy(() => import('features/admin/pages/AdminGymApprovals'));
const AdminIncompleteSetups = lazy(() => import('features/admin/pages/AdminIncompleteSetups'));
const AdminMyClaimedEvents = lazy(() => import('features/admin/pages/AdminMyClaimedEvents'));
const AdminMergeGyms = lazy(() => import('features/admin/pages/AdminMergeGyms'));
const AdminMergeEvents = lazy(() => import('features/admin/pages/AdminMergeEvents'));
const AdminImportGyms = lazy(() => import('features/admin/pages/AdminImportGyms'));
const AdminMatchEvents = lazy(() => import('features/admin/pages/AdminMatchEvents'));
const AdminOutreach = lazy(() => import('features/admin/pages/AdminOutreach'));
const AdminBackfillSchedules = lazy(() => import('features/admin/pages/AdminBackfillSchedules'));
const AdminSeedIBJJF = lazy(() => import('features/admin/pages/AdminSeedIBJJF'));
const AdminUsers = lazy(() => import('features/admin/pages/AdminUsers'));
const AdminExport = lazy(() => import('features/admin/pages/AdminExport'));

// Wraps an element in AdminRoute — keeps the admin route table scannable.
const admin = (el) => <AdminRoute>{el}</AdminRoute>;

// Same markup the guards render while auth loads, so the lazy-load fallback
// is visually one continuous state (no double flash).
const PageFallback = () => <div className="page-loading" />;

const AppRoutes = () => (
  <Layout>
    <RouteErrorBoundary>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Public */}
          <Route path={ROUTES.home} element={<Landing />} />
          <Route path={ROUTES.discover} element={<Discover />} />
          <Route path={ROUTE_PATTERNS.eventDetail} element={<EventDetail />} />
          <Route path={ROUTE_PATTERNS.cityEvents} element={<CityEvents />} />
          <Route path={ROUTE_PATTERNS.cityDiscipline} element={<CityEvents />} />
          <Route path={ROUTE_PATTERNS.eventDetailLegacy} element={<EventDetail />} />
          <Route path={ROUTE_PATTERNS.profileById} element={<PublicProfile />} />
          <Route path={ROUTE_PATTERNS.gymBySlug} element={<PublicProfile />} />
          <Route
            path={ROUTES.login}
            element={
              <GuestRoute>
                <Login />
              </GuestRoute>
            }
          />
          <Route
            path={ROUTES.signup}
            element={
              <GuestRoute>
                <Signup />
              </GuestRoute>
            }
          />
          <Route path={ROUTES.verifyEmail} element={<VerifyEmail />} />
          <Route path={ROUTES.resetPassword} element={<ResetPassword />} />
          <Route path="/booking/success" element={<BookingSuccess />} />
          <Route path={ROUTES.terms} element={<TermsOfService />} />
          <Route path={ROUTES.privacy} element={<PrivacyPolicy />} />
          <Route path={ROUTES.impressum} element={<Impressum />} />
          <Route path={ROUTES.cookies} element={<CookiePolicy />} />
          <Route path={ROUTES.support} element={<Support />} />
          <Route path={ROUTES.openMatEvents} element={<OpenMatEvents />} />
          <Route path={ROUTES.camp} element={<CampLanding />} />
          <Route path={ROUTES.campTerms} element={<CampTerms />} />
          <Route path={ROUTES.campBooked} element={<CampBooked />} />

          {/* Student protected */}
          <Route
            path={ROUTES.myBookings}
            element={
              <PrivateRoute>
                <MyBookings />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.savedEvents}
            element={
              <PrivateRoute>
                <SavedEvents />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.account}
            element={
              <PrivateRoute>
                <Account />
              </PrivateRoute>
            }
          />

          {/* Management protected */}
          <Route
            path={ROUTES.gymDashboard}
            element={
              <GymOwnerRoute>
                <GymDashboard />
              </GymOwnerRoute>
            }
          />
          <Route
            path={ROUTES.stripeConnectReturn}
            element={
              <PrivateRoute>
                <StripeConnectReturn />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.stripeConnectRefresh}
            element={
              <PrivateRoute>
                <StripeConnectRefresh />
              </PrivateRoute>
            }
          />
          <Route path={ROUTES.createEvent} element={<CreateEvent />} />
          <Route
            path={ROUTE_PATTERNS.editEvent}
            element={
              <EventEditorRoute>
                <EditEvent />
              </EventEditorRoute>
            }
          />
          <Route
            path={ROUTES.profile}
            element={
              <GymOwnerRoute>
                <GymProfile />
              </GymOwnerRoute>
            }
          />
          <Route
            path={ROUTE_PATTERNS.gymProfileLegacy}
            element={<Navigate to={ROUTES.profile} replace />}
          />

          {/* Admin protected */}
          <Route path={ROUTES.admin.hub} element={admin(<AdminHub />)} />
          <Route path={ROUTES.admin.events} element={admin(<AdminEvents />)} />
          <Route path={ROUTES.admin.ownEvents} element={admin(<AdminOwnEvents />)} />
          <Route path={ROUTES.admin.gyms} element={admin(<AdminGyms />)} />
          <Route path={ROUTES.admin.gymClaims} element={admin(<AdminGymClaims />)} />
          <Route path={ROUTES.admin.gymApprovals} element={admin(<AdminGymApprovals />)} />
          <Route path={ROUTES.admin.incompleteSetups} element={admin(<AdminIncompleteSetups />)} />
          <Route path={ROUTES.admin.myClaimedEvents} element={admin(<AdminMyClaimedEvents />)} />
          <Route path={ROUTES.admin.mergeGyms} element={admin(<AdminMergeGyms />)} />
          <Route path={ROUTES.admin.mergeEvents} element={admin(<AdminMergeEvents />)} />
          <Route path={ROUTES.admin.importGyms} element={admin(<AdminImportGyms />)} />
          <Route path={ROUTES.admin.matchEvents} element={admin(<AdminMatchEvents />)} />
          <Route path={ROUTES.admin.outreach} element={admin(<AdminOutreach />)} />
          <Route
            path={ROUTES.admin.backfillSchedules}
            element={admin(<AdminBackfillSchedules />)}
          />
          <Route path={ROUTES.admin.seedIbjjf} element={admin(<AdminSeedIBJJF />)} />
          <Route path={ROUTES.admin.users} element={admin(<AdminUsers />)} />
          <Route path={ROUTES.admin.dataExport} element={admin(<AdminExport />)} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  </Layout>
);

export default AppRoutes;
